/**
 * @fileoverview Formula expression validator
 * 
 * Pre-validates formula expressions to catch common errors before evaluation:
 * - Unknown function calls (e.g., "filtering" instead of "filter")
 * - Undefined variable references in lambda functions
 * - Provides helpful suggestions and error messages
 */

import { UnknownFunctionError, UndefinedVariableError, ReservedWordError } from './errors.js';
import { BUILTIN_NAMES } from './builtins.js';

/**
 * Calculates Levenshtein distance between two strings
 * Used for suggesting similar function names when unknown functions are used
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} - Edit distance between the strings
 */
function levenshteinDistance(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Suggests similar function names based on edit distance
 * @param {string} unknownFunction - The unknown function name
 * @param {number} maxSuggestions - Maximum number of suggestions to return
 * @returns {string[]} - Array of suggested function names
 */
function suggestSimilarFunctions(unknownFunction, maxSuggestions = 3) {
  const suggestions = [];
  const builtinNames = Array.from(BUILTIN_NAMES);
  
  for (const functionName of builtinNames) {
    const distance = levenshteinDistance(unknownFunction.toLowerCase(), functionName.toLowerCase());
    const longerLength = Math.max(unknownFunction.length, functionName.length);
    
    // Use a more flexible distance calculation:
    // - Allow up to 3 character differences for longer words
    // - Use ratio-based distance for better matching
    const maxDistance = Math.max(2, Math.min(4, Math.floor(longerLength * 0.4)));
    const distanceRatio = distance / longerLength;
    
    // Accept if distance is reasonable OR if one string contains the other
    const oneContainsOther = unknownFunction.toLowerCase().includes(functionName.toLowerCase()) ||
                             functionName.toLowerCase().includes(unknownFunction.toLowerCase());
    
    if (distance <= maxDistance || distanceRatio <= 0.4 || oneContainsOther) {
      suggestions.push({ name: functionName, distance });
    }
  }
  
  // Sort by distance and return the closest matches
  return suggestions
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxSuggestions)
    .map(s => s.name);
}

/**
 * Analyzes an expression to find function calls and their arguments
 * @param {string} expression - The formula expression to analyze
 * @returns {Object[]} - Array of function call objects with name, args, and position info
 */
function analyzeFunctionCalls(expression) {
  const functionCalls = [];
  
  // Enhanced regex to match function calls with their arguments
  // Matches: functionName(arguments...)
  const functionCallPattern = /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g;
  let match;
  
  while ((match = functionCallPattern.exec(expression)) !== null) {
    const functionName = match[1];
    const startPos = match.index;
    const openParenPos = startPos + functionName.length;
    
    // Find the matching closing parenthesis
    let parenCount = 1;
    let endPos = openParenPos + 1;
    let argsContent = '';
    
    while (endPos < expression.length && parenCount > 0) {
      const char = expression[endPos];
      if (char === '(') {
        parenCount++;
      } else if (char === ')') {
        parenCount--;
      }
      
      if (parenCount > 0) {
        argsContent += char;
      }
      endPos++;
    }
    
    functionCalls.push({
      name: functionName,
      startPos,
      endPos: endPos - 1,
      argsContent: argsContent.trim()
    });
  }
  
  return functionCalls;
}

/**
 * Analyzes lambda function arguments to extract parameter names
 * @param {string} argsContent - The arguments content of a function call
 * @returns {Object[]} - Array of lambda function info with parameter names and body
 */
function analyzeLambdaFunctions(argsContent) {
  const lambdas = [];
  
  if (!argsContent || typeof argsContent !== 'string') {
    return lambdas;
  }
  
  // Track processed positions to avoid double-processing overlapping lambdas
  const processedRanges = [];
  
  // Find arrow functions by looking for the => pattern and then working backwards to find params
  const arrowPattern = /=>/g;
  let match;
  
  while ((match = arrowPattern.exec(argsContent)) !== null) {
    const arrowIndex = match.index;
    
    // Check if this arrow is already part of a processed lambda
    const isAlreadyProcessed = processedRanges.some(range => 
      arrowIndex >= range.start && arrowIndex <= range.end
    );
    if (isAlreadyProcessed) continue;
    
    // Work backwards from the arrow to find the parameter part
    let paramStart = arrowIndex - 1;
    let parenCount = 0;
    let foundParam = false;
    
    // Skip whitespace before =>
    while (paramStart >= 0 && /\s/.test(argsContent[paramStart])) {
      paramStart--;
    }
    
    if (paramStart < 0) continue;
    
    // Check if it ends with a closing parenthesis (multi-param lambda)
    if (argsContent[paramStart] === ')') {
      parenCount = 1;
      paramStart--;
      
      // Find the matching opening parenthesis
      while (paramStart >= 0 && parenCount > 0) {
        if (argsContent[paramStart] === ')') parenCount++;
        else if (argsContent[paramStart] === '(') parenCount--;
        paramStart--;
      }
      
      if (parenCount === 0) {
        foundParam = true;
      }
    } else {
      // Single parameter without parentheses
      while (paramStart >= 0 && /[a-zA-Z0-9_$]/.test(argsContent[paramStart])) {
        paramStart--;
      }
      foundParam = true;
    }
    
    if (!foundParam) continue;
    
    // Extract the parameter part
    const paramPart = argsContent.substring(paramStart + 1, arrowIndex).trim();
    
    // Extract parameters
    let parameters = [];
    if (paramPart.startsWith('(') && paramPart.endsWith(')')) {
      // Multi-parameter: (param1, param2)
      const innerParams = paramPart.slice(1, -1);
      parameters = innerParams.split(',').map(p => p.trim()).filter(p => p.length > 0);
    } else {
      // Single parameter
      parameters = [paramPart];
    }
    
    // Find the body after the arrow
    let bodyStart = arrowIndex + 2; // Skip =>
    while (bodyStart < argsContent.length && /\s/.test(argsContent[bodyStart])) {
      bodyStart++;
    }
    
    // Find the end of the lambda body by balanced parentheses/brackets counting
    let bodyEnd = bodyStart;
    let depth = 0;
    let inString = false;
    let stringChar = null;
    
    while (bodyEnd < argsContent.length) {
      const char = argsContent[bodyEnd];
      
      if (!inString) {
        if (char === '"' || char === "'") {
          inString = true;
          stringChar = char;
        } else if (char === '(' || char === '[' || char === '{') {
          depth++;
        } else if (char === ')' || char === ']' || char === '}') {
          depth--;
          // If we go negative, we've hit the end of the lambda's context
          if (depth < 0) break;
        } else if (char === ',' && depth === 0) {
          // Comma at top level indicates argument separator
          break;
        }
      } else if (char === stringChar && argsContent[bodyEnd - 1] !== '\\') {
        inString = false;
        stringChar = null;
      }
      
      bodyEnd++;
    }
    
    const body = argsContent.substring(bodyStart, bodyEnd).trim();
    
    if (parameters.length > 0 && body) {
      // Check that parameters are valid identifiers
      const validParams = parameters.filter(param => /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(param));
      
      if (validParams.length > 0) {
        const fullMatch = argsContent.substring(paramStart + 1, bodyEnd);
        
        lambdas.push({
          parameters: validParams,
          body: body,
          fullMatch: fullMatch
        });
        
        // Track this range as processed
        processedRanges.push({
          start: paramStart + 1,
          end: bodyEnd
        });
      }
    }
  }
  
  return lambdas;
}

/**
 * Extracts all identifiers (variable names) from an expression, excluding property accesses
 * @param {string} expression - Expression to analyze
 * @returns {Set<string>} - Set of identifier names found in the expression
 */
function extractIdentifiers(expression) {
  const identifiers = new Set();
  
  // Remove string literals first to avoid false positives
  let cleanExpression = expression.replace(/(["'])((?:\\.|(?!\1)[^\\])*?)\1/g, '""');
  
  // Remove object literal keys to avoid treating them as variables
  // This handles cases like { label: value, x: value } where label and x are keys, not variables
  // We need to be careful to only remove keys, not values
  cleanExpression = cleanExpression.replace(/\{([^}]*)\}/g, (match, content) => {
    // Process the content inside braces to remove object keys
    let cleanedContent = content;
    // Match key: value patterns and replace the key part
    cleanedContent = cleanedContent.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, (keyMatch, key) => {
      // Replace the key with a placeholder to avoid it being detected as an identifier
      return '__OBJ_KEY__:';
    });
    return '{' + cleanedContent + '}';
  });
  
  // Find property access chains and remove them, keeping only the base objects
  // This handles cases like obj.prop1.prop2.prop3 where only "obj" should be a variable
  const propertyChainPattern = /\b([a-zA-Z_$][a-zA-Z0-9_$]*)(\.[a-zA-Z_$][a-zA-Z0-9_$]*)+/g;
  const baseObjects = new Set();
  
  // Extract base objects from property chains
  let propMatch;
  while ((propMatch = propertyChainPattern.exec(cleanExpression)) !== null) {
    const baseObject = propMatch[1];
    baseObjects.add(baseObject);
  }
  
  // Remove property chains from the expression to avoid processing property names as variables
  cleanExpression = cleanExpression.replace(propertyChainPattern, (match, baseObj) => baseObj);
  
  // Find all remaining identifiers
  const identifierPattern = /\b[a-zA-Z_$][a-zA-Z0-9_$]*\b/g;
  let match;
  
  while ((match = identifierPattern.exec(cleanExpression)) !== null) {
    const identifier = match[0];
    
    // Skip JavaScript keywords and numbers
    const jsKeywords = new Set([
      'true', 'false', 'null', 'undefined', 'NaN', 'Infinity',
      'let', 'const', 'var', 'function', 'return', 'else', 'if', 'for', 'while'
    ]);
    
    // Skip if it's a number or keyword (but NOT built-in names - we need to check those)
    if (jsKeywords.has(identifier) || !isNaN(Number(identifier))) {
      continue;
    }
    
    // Skip our placeholder
    if (identifier === '__OBJ_KEY__') {
      continue;
    }
    
    identifiers.add(identifier);
  }
  
  // Add back the base objects from property chains
  baseObjects.forEach(obj => identifiers.add(obj));
  
  return identifiers;
}

/**
 * Collects all lambda functions from an expression with their nesting levels
 * @param {string} expression - Expression to analyze
 * @returns {Object[]} - Array of lambda info with nesting context
 */
function collectAllLambdas(expression) {
  const allLambdas = [];
  const processedBodies = new Set(); // Prevent processing the same lambda body multiple times
  
  function collectFromFunctionCalls(expr, outerParams = []) {
    const functionCalls = analyzeFunctionCalls(expr);
    
    for (const funcCall of functionCalls) {
      if (!funcCall.argsContent) continue;
      
      const lambdas = analyzeLambdaFunctions(funcCall.argsContent);
      
      for (const lambda of lambdas) {
        if (lambda.parameters.length === 0) continue;
        
        // Create a unique key for this lambda to prevent duplicates
        const lambdaKey = `${lambda.parameters.join(',')}->${lambda.body}`;
        if (processedBodies.has(lambdaKey)) continue;
        processedBodies.add(lambdaKey);
        
        // Add context about available parameters from outer scopes
        const lambdaWithContext = {
          ...lambda,
          availableParams: [...outerParams, ...lambda.parameters],
          debugKey: lambdaKey
        };
        
        allLambdas.push(lambdaWithContext);
        
        // Recursively collect lambdas from this lambda's body
        collectFromFunctionCalls(lambda.body, lambdaWithContext.availableParams);
      }
    }
  }
  
  collectFromFunctionCalls(expression);
  return allLambdas;
}

/**
 * Validates a formula expression for common errors
 * @param {string} expression - Formula expression to validate
 * @param {string} formulaId - ID of the formula for error reporting
 * @param {Set<string>} validFieldIds - Set of valid field IDs in the document
 * @returns {Object[]} - Array of validation errors found
 */
export function validateFormula(expression, formulaId, validFieldIds = new Set()) {
  const errors = [];
  
  if (!expression || typeof expression !== 'string') {
    return errors;
  }
  
  // Preprocess expression (convert arrow syntax like evaluator does)
  let processedExpression = expression.replace(/->/g, '=>');
  processedExpression = processedExpression.replace(/\bif\s*\(/g, 'iff(');
  
  // 1. Check for unknown function calls
  const functionCalls = analyzeFunctionCalls(processedExpression);
  
  for (const funcCall of functionCalls) {
    const { name, argsContent } = funcCall;
    
    // Skip if it's a valid built-in function or field reference
    if (BUILTIN_NAMES.has(name) || validFieldIds.has(name)) {
      continue;
    }
    
    // Check if it looks like a function call (has arguments)
    if (argsContent !== undefined) {
      const suggestions = suggestSimilarFunctions(name);
      errors.push(new UnknownFunctionError(name, formulaId, suggestions));
    }
  }
  
  // 2. Check for reserved words used as variables (not function calls)
  // Extract all identifiers that are not part of function calls
  const identifiers = extractIdentifiers(processedExpression);
  const functionCallNames = new Set(functionCalls.filter(fc => fc.argsContent !== undefined).map(fc => fc.name));
  
  for (const identifier of identifiers) {
    // Check if it's a builtin name but NOT used as a function call
    if (BUILTIN_NAMES.has(identifier) && !functionCallNames.has(identifier) && !validFieldIds.has(identifier)) {
      errors.push(new ReservedWordError(identifier, formulaId));
    }
  }
  
  // 3. Collect all lambdas with their nested scope context
  const allLambdas = collectAllLambdas(processedExpression);
  const seenUndefinedVars = new Set(); // Prevent duplicate error reporting
  

  
  for (const lambda of allLambdas) {
    // Available variables = lambda params + outer scope params + field IDs
    const availableVars = [...lambda.availableParams, ...Array.from(validFieldIds)];
    
    // Create a version of the lambda body with nested lambda expressions removed
    // This prevents us from validating variables that are inside nested lambdas
    let bodyForValidation = lambda.body;
    const nestedLambdas = analyzeLambdaFunctions(lambda.body);
    
    // Sort nested lambdas by position (reverse order) so we can remove them without affecting positions
    const sortedNestedLambdas = nestedLambdas
      .map(nested => ({
        ...nested,
        startPos: lambda.body.indexOf(nested.fullMatch)
      }))
      .filter(nested => nested.startPos !== -1)
      .sort((a, b) => b.startPos - a.startPos);
    
    // Remove nested lambda expressions from the body
    for (const nestedLambda of sortedNestedLambdas) {
      const beforePos = nestedLambda.startPos;
      const afterPos = beforePos + nestedLambda.fullMatch.length;
      bodyForValidation = bodyForValidation.substring(0, beforePos) + 
                         ' __NESTED_LAMBDA__ ' + 
                         bodyForValidation.substring(afterPos);
    }
    
    // Check for property base identifiers (e.g., "row" in "row.text1") in the cleaned body
    // Only match identifiers that are actual base objects (not preceded by a dot)
    const propertyBasePattern = /(?:^|[^a-zA-Z0-9_$.])([a-zA-Z_$][a-zA-Z0-9_$]*)\./g;
    let match;
    
    while ((match = propertyBasePattern.exec(bodyForValidation)) !== null) {
      const identifier = match[1];
      
      const isAvailable = availableVars.includes(identifier) || 
                         BUILTIN_NAMES.has(identifier) ||
                         identifier === '__NESTED_LAMBDA__';
      const errorKey = `${lambda.fullMatch}-${identifier}`;
      
      if (!isAvailable && !seenUndefinedVars.has(errorKey)) {
        seenUndefinedVars.add(errorKey);
        errors.push(new UndefinedVariableError(
          identifier,
          formulaId,
          availableVars
        ));
      }
    }
    
    // Check standalone identifiers in the cleaned body
    const usedIdentifiers = extractIdentifiers(bodyForValidation);
    

    
    for (const identifier of usedIdentifiers) {
      const isAvailable = availableVars.includes(identifier) || 
                         BUILTIN_NAMES.has(identifier) ||
                         identifier === '__NESTED_LAMBDA__';
      const errorKey = `${lambda.fullMatch}-${identifier}`;
      
      if (!isAvailable && !seenUndefinedVars.has(errorKey)) {
        // Be conservative: only flag simple identifiers that look like lambda parameters
        if (/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(identifier) && identifier.length > 1) {
          seenUndefinedVars.add(errorKey);
          errors.push(new UndefinedVariableError(
            identifier,
            formulaId,
            availableVars
          ));
        }
      }
    }
  }
  
  return errors;
} 